/**
 * US Government Data Source Integrations
 * 
 * Comprehensive integrations for:
 * - USASpending API (Awards, Budgets, Spending)
 * - Federal Register (Executive Orders, Laws, Regulations)
 * - RSS Feeds (Live news tracking)
 * - Department Budgeting and Finance
 * 
 * Based on GovtTenderAI/govearner-dashboard implementations
 */

import { withRetry } from './retryUtils';
import { Opportunity, Award, DataSourceConfig } from './dataSourceIntegrations';

// ============================================================================
// Type Definitions
// ============================================================================

export interface USAward {
  id: string;
  award_id: string;
  recipient_name: string;
  award_amount: number;
  start_date: string;
  end_date: string;
  awarding_agency: string;
  awarding_sub_agency: string;
  funding_agency: string;
  award_type: string;
  naics_code?: string;
  description?: string;
  url: string;
}

export interface ExecutiveOrder {
  id: string;
  document_number: string;
  title: string;
  president: string;
  publication_date: string;
  executive_order_number?: string;
  type: 'PRESDOCU';
  agencies: string[];
  html_url: string;
  pdf_url: string;
  summary?: string;
}

export interface FederalRegisterDocument {
  id: string;
  document_number: string;
  title: string;
  type: string;
  publication_date: string;
  agencies: string[];
  html_url: string;
  pdf_url: string;
  summary?: string;
  regulation_type?: string;
}

export interface RSSFeedItem {
  id: string;
  title: string;
  description: string;
  link: string;
  pubDate: string;
  source: string;
  category?: string[];
  guid?: string;
}

export interface DepartmentBudget {
  agency_name: string;
  agency_code: string;
  fiscal_year: number;
  total_budgetary_resources: number;
  total_obligations: number;
  total_outlays: number;
  budget_function?: string;
  budget_subfunction?: string;
}

// ============================================================================
// USASpending API Integration
// ============================================================================

const USASPENDING_BASE_URL = 'https://api.usaspending.gov/api/v2';

/**
 * Fetch US Government Awards from USASpending API
 * Includes FPDS (Federal Procurement Data System) data
 */
export async function fetchUSAwards(
  config: DataSourceConfig,
  projectId: string
): Promise<{ opportunities: Opportunity[]; awards: Award[] }> {
  const opportunities: Opportunity[] = [];
  const awards: Award[] = [];
  
  try {
    console.log(`[LIVE] Fetching US Awards from USASpending API...`);
    
    // Build search filters - USASpending API requires specific format
    const now = new Date();
    const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
    
    const filters: any = {
      award_type_codes: ['A', 'B', 'C', 'D'], // Contracts only
      time_period: [
        {
          start_date: oneYearAgo.toISOString().split('T')[0],
          end_date: now.toISOString().split('T')[0],
          date_type: 'action_date',
        },
      ],
    };
    
    // Add keyword search if specified (USASpending uses 'keywords' array)
    if (config.filters?.keywords && config.filters.keywords.length > 0) {
      // Filter out short keywords (API requires min 3 chars)
      const validKeywords = config.filters.keywords.filter(k => k && k.length >= 3);
      if (validKeywords.length > 0) {
        filters.keywords = validKeywords;
      }
    }
    
    const payload = {
      filters: filters,
      fields: [
        'Award ID',
        'Recipient Name',
        'Start Date',
        'End Date',
        'Award Amount',
        'Awarding Agency',
        'Awarding Sub Agency',
        'Award Type',
        'Funding Agency',
        'Funding Sub Agency',
      ],
      sort: 'Award Amount',
      order: 'desc',
      limit: 100,
      page: 1,
    };
    
    const response = await withRetry(
      () => fetch(`${USASPENDING_BASE_URL}/search/spending_by_award/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }),
      {
        maxRetries: 3,
        initialDelayMs: 1000,
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[LIVE] USASpending API error response:`, errorText);
      throw new Error(`USASpending API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Handle API errors in response
    if (data.error) {
      throw new Error(`USASpending API error: ${data.error}`);
    }
    const results = data.results || [];
    
    console.log(`[LIVE] Parsing ${results.length} US Awards...`);
    
    for (const item of results) {
      const awardId = item['Award ID'] || item.award_id || `usa-${Date.now()}-${Math.random()}`;
      const recipientName = item['Recipient Name'] || item.recipient_name || 'Unknown';
      const amount = parseFloat(item['Award Amount'] || item.award_amount || '0');
      const startDate = item['Start Date'] || item.start_date || '';
      const endDate = item['End Date'] || item.end_date || '';
      const awardingAgency = item['Awarding Agency'] || item.awarding_agency || 'US Federal Government';
      const description = item['Description'] || item.description || '';
      const naicsCode = item['NAICS Code'] || item.naics_code || '';
      
      // Create opportunity (if award is recent/future)
      const endDateObj = endDate ? new Date(endDate) : null;
      const isActive = !endDateObj || endDateObj > new Date();
      
      if (isActive) {
        opportunities.push({
          id: `usa-opp-${awardId}`,
          source: 'samgov', // Using samgov as source type for US opportunities
          title: `${awardingAgency} - ${recipientName}`,
          description: description.substring(0, 5000),
          organization: awardingAgency,
          deadline: endDate || startDate,
          value: amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
          url: `https://www.usaspending.gov/award/${awardId}`,
          keywords: extractKeywords(description),
          service_lines: matchServiceLines(description),
          reference_number: awardId,
          status: 'awarded',
          naics_code: naicsCode,
          location: {
            country: 'United States',
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
      
      // Create award record
      awards.push({
        id: `usa-award-${awardId}`,
        source: 'samgov',
        opportunity_id: `usa-opp-${awardId}`,
        title: `${awardingAgency} - ${recipientName}`,
        organization: awardingAgency,
        awardee: recipientName,
        award_date: startDate,
        award_value: amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
        award_number: awardId,
        description: description.substring(0, 2000),
        url: `https://www.usaspending.gov/award/${awardId}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }
    
    console.log(`[LIVE] Fetched ${opportunities.length} opportunities and ${awards.length} awards from USASpending`);
  } catch (error: any) {
    console.error('[LIVE] Error fetching US Awards:', error);
    throw error;
  }
  
  return { opportunities, awards };
}

/**
 * Fetch Department Budgets from USASpending API
 */
export async function fetchDepartmentBudgets(
  config: DataSourceConfig,
  projectId: string
): Promise<DepartmentBudget[]> {
  const budgets: DepartmentBudget[] = [];
  
  try {
    console.log(`[LIVE] Fetching Department Budgets from USASpending API...`);
    
    // Get current fiscal year
    const now = new Date();
    const fiscalYear = now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear();
    
    // Get top-tier agencies
    const agenciesResponse = await withRetry(
      () => fetch(`${USASPENDING_BASE_URL}/references/toptier_agencies/`),
      {
        maxRetries: 3,
        initialDelayMs: 1000,
      }
    );
    
    if (!agenciesResponse.ok) {
      throw new Error(`USASpending API error: ${agenciesResponse.statusText}`);
    }
    
    const agenciesData = await agenciesResponse.json();
    const agencies = agenciesData.results || [];
    
    // Fetch budget for each agency
    for (const agency of agencies.slice(0, 20)) { // Limit to top 20 for performance
      try {
        const budgetResponse = await withRetry(
          () => fetch(`${USASPENDING_BASE_URL}/references/total_budgetary_resources/?year=${fiscalYear}&agency=${agency.toptier_code}`),
          {
            maxRetries: 2,
            initialDelayMs: 500,
          }
        );
        
        if (budgetResponse.ok) {
          const budgetData = await budgetResponse.json();
          if (budgetData.results && budgetData.results.length > 0) {
            const budget = budgetData.results[0];
            budgets.push({
              agency_name: agency.name || agency.agency_name,
              agency_code: agency.toptier_code || agency.agency_code,
              fiscal_year: fiscalYear,
              total_budgetary_resources: parseFloat(budget.total_budgetary_resources || '0'),
              total_obligations: parseFloat(budget.total_obligations || '0'),
              total_outlays: parseFloat(budget.total_outlays || '0'),
            });
          }
        }
      } catch (error) {
        console.warn(`Failed to fetch budget for ${agency.name}:`, error);
      }
    }
    
    console.log(`[LIVE] Fetched ${budgets.length} department budgets`);
  } catch (error: any) {
    console.error('[LIVE] Error fetching Department Budgets:', error);
    throw error;
  }
  
  return budgets;
}

// ============================================================================
// Federal Register Integration
// ============================================================================

const FEDERAL_REGISTER_BASE_URL = 'https://www.federalregister.gov/api/v1';

/**
 * Fetch Executive Orders from Federal Register
 */
export async function fetchExecutiveOrders(
  config: DataSourceConfig,
  projectId: string
): Promise<ExecutiveOrder[]> {
  const orders: ExecutiveOrder[] = [];
  
  try {
    console.log(`[LIVE] Fetching Executive Orders from Federal Register...`);
    
    const params = new URLSearchParams({
      per_page: '100',
      page: '1',
      'conditions[type]': 'PRESDOCU',
      'conditions[presidential_document_type_id]': '2', // Executive Orders
      'conditions[correction]': '0',
      order: 'publication_date',
      direction: 'desc',
    });
    
    // Add date filter (last 2 years)
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    params.append('conditions[publication_date][gte]', twoYearsAgo.toISOString().split('T')[0]);
    
    // Add keyword filter if specified
    if (config.filters?.keywords && config.filters.keywords.length > 0) {
      params.append('term', config.filters.keywords.join(' '));
    }
    
    const response = await withRetry(
      () => fetch(`${FEDERAL_REGISTER_BASE_URL}/articles.json?${params}`),
      {
        maxRetries: 3,
        initialDelayMs: 1000,
      }
    );
    
    if (!response.ok) {
      throw new Error(`Federal Register API error: ${response.statusText}`);
    }
    
    const data = await response.json();
    const results = data.results || [];
    
    console.log(`[LIVE] Parsing ${results.length} Executive Orders...`);
    
    for (const item of results) {
      orders.push({
        id: `eo-${item.document_number}`,
        document_number: item.document_number,
        title: item.title,
        president: item.president || 'Unknown',
        publication_date: item.publication_date,
        executive_order_number: item.executive_order_number,
        type: 'PRESDOCU',
        agencies: item.agencies?.map((a: any) => a.name) || [],
        html_url: item.html_url,
        pdf_url: item.pdf_url,
        summary: item.abstract,
      });
    }
    
    console.log(`[LIVE] Fetched ${orders.length} Executive Orders`);
  } catch (error: any) {
    console.error('[LIVE] Error fetching Executive Orders:', error);
    throw error;
  }
  
  return orders;
}

/**
 * Fetch Federal Register Documents (Laws, Regulations, Notices)
 */
export async function fetchFederalRegisterDocuments(
  config: DataSourceConfig,
  projectId: string
): Promise<FederalRegisterDocument[]> {
  const documents: FederalRegisterDocument[] = [];
  
  try {
    console.log(`[LIVE] Fetching Federal Register Documents...`);
    
    const params = new URLSearchParams({
      per_page: '100',
      page: '1',
      order: 'publication_date',
      direction: 'desc',
    });
    
    // Add type filters
    const types = ['RULE', 'PRORULE', 'NOTICE', 'PRESDOCU'];
    types.forEach(type => params.append('conditions[type][]', type));
    
    // Add date filter (last year)
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    params.append('conditions[publication_date][gte]', oneYearAgo.toISOString().split('T')[0]);
    
    // Add keyword filter if specified
    if (config.filters?.keywords && config.filters.keywords.length > 0) {
      params.append('term', config.filters.keywords.join(' '));
    }
    
    const response = await withRetry(
      () => fetch(`${FEDERAL_REGISTER_BASE_URL}/documents.json?${params}`),
      {
        maxRetries: 3,
        initialDelayMs: 1000,
      }
    );
    
    if (!response.ok) {
      throw new Error(`Federal Register API error: ${response.statusText}`);
    }
    
    const data = await response.json();
    const results = data.results || [];
    
    console.log(`[LIVE] Parsing ${results.length} Federal Register Documents...`);
    
    for (const item of results) {
      documents.push({
        id: `fr-${item.document_number}`,
        document_number: item.document_number,
        title: item.title,
        type: item.type,
        publication_date: item.publication_date,
        agencies: item.agencies?.map((a: any) => a.name) || [],
        html_url: item.html_url,
        pdf_url: item.pdf_url,
        summary: item.abstract,
        regulation_type: item.regulation_type,
      });
    }
    
    console.log(`[LIVE] Fetched ${documents.length} Federal Register Documents`);
  } catch (error: any) {
    console.error('[LIVE] Error fetching Federal Register Documents:', error);
    throw error;
  }
  
  return documents;
}

// ============================================================================
// RSS Feed Integration
// ============================================================================

/**
 * Fetch RSS Feed Items
 * Supports multiple RSS feeds for live news tracking
 */
export async function fetchRSSFeeds(
  config: DataSourceConfig,
  projectId: string
): Promise<RSSFeedItem[]> {
  let items: RSSFeedItem[] = []; // Changed from const to let to allow reassignment
  
  // Import RSS feed registry
  const { RSS_FEED_REGISTRY, getEnabledFeeds } = await import('./rssFeedRegistry');
  
  // Get feeds based on config
  let selectedFeeds = getEnabledFeeds();
  
  // If specific feed IDs are provided, filter by those
  if (config.filters?.feedIds && config.filters.feedIds.length > 0) {
    selectedFeeds = RSS_FEED_REGISTRY.filter(
      feed => feed.enabled && config.filters?.feedIds?.includes(feed.id)
    );
  }
  // Otherwise, if specific categories are requested, filter by those
  else if (config.filters?.categories && config.filters.categories.length > 0) {
    selectedFeeds = RSS_FEED_REGISTRY.filter(
      feed => feed.enabled && config.filters?.categories?.includes(feed.category)
    );
  }
  
  // Extract URLs
  const feedUrls = selectedFeeds.map(feed => feed.url);
  
  console.log(`[LIVE] Fetching ${feedUrls.length} RSS feeds from verified registry`);
  
  // Get API base URL for backend proxy
  const { getApiBaseUrl } = await import('@tracekit/core');
  const apiBaseUrl = getApiBaseUrl();
  
  try {
    console.log(`[LIVE] Fetching ${feedUrls.length} RSS feeds in PARALLEL with keywords:`, config.filters?.keywords);
    
    // Fetch all feeds in parallel with individual timeouts
    const fetchPromises = feedUrls.map(async (feedUrl) => {
      try {
        // Use backend proxy to avoid CORS issues - backend returns JSON
        const proxyUrl = `${apiBaseUrl}/api/data-sources/rss/proxy?url=${encodeURIComponent(feedUrl)}&format=json`;
        
        // Create a timeout promise (10 seconds per feed - government sites can be slow)
        const timeoutPromise = new Promise<Response>((_, reject) => {
          setTimeout(() => reject(new Error('Feed timeout after 10s')), 10000);
        });
        
        // Race between fetch and timeout
        const fetchPromise = fetch(proxyUrl, {
          headers: {
            'Accept': 'application/json',
          },
          signal: AbortSignal.timeout ? AbortSignal.timeout(10000) : undefined,
        });
        
        const response = await Promise.race([fetchPromise, timeoutPromise]);
        
        if (!response.ok) {
          console.warn(`[LIVE] RSS feed ${feedUrl} returned ${response.status}`);
          return [];
        }
        
        // Backend proxy returns parsed JSON
        const data = await response.json();
        const opportunities = data.opportunities || [];
        
        // Convert backend format to RSSFeedItem format
        const feedItems: RSSFeedItem[] = opportunities.map((opp: any) => ({
          id: opp.id || `rss-${Date.now()}-${Math.random()}`,
          title: opp.title || 'Untitled',
          description: opp.description || '',
          link: opp.url || feedUrl,
          pubDate: opp.published_date || new Date().toISOString(),
          source: opp.source || feedUrl,
          guid: opp.id || `rss-${Date.now()}-${Math.random()}`,
        }));
        
        console.log(`[LIVE] ✅ Fetched ${feedItems.length} items from ${feedUrl}`);
        return feedItems;
      } catch (error) {
        console.warn(`[LIVE] ⚠️ Skipped ${feedUrl}:`, error instanceof Error ? error.message : 'Unknown error');
        return [];
      }
    });
    
    // Wait for all feeds to complete (with individual timeouts)
    const startTime = Date.now();
    const results = await Promise.all(fetchPromises);
    const duration = Date.now() - startTime;
    
    // Flatten all results
    items = results.flat();
    
    console.log(`[LIVE] ⚡ Fetched ${items.length} total items from ${feedUrls.length} feeds in ${duration}ms`)
    
    // ALWAYS filter by keywords if specified - this is critical for curated content
    let filteredItems = items;
    if (config.filters?.keywords && config.filters.keywords.length > 0) {
      const keywords = config.filters.keywords.map(k => k.toLowerCase());
      
      filteredItems = items.filter(item => {
        const searchText = `${item.title} ${item.description}`.toLowerCase();
        // Item must match at least one keyword
        return keywords.some(keyword => searchText.includes(keyword));
      });
      
      console.log(`[LIVE] Filtered ${items.length} items to ${filteredItems.length} based on keywords:`, keywords);
    } else {
      console.warn('[LIVE] No keywords specified - returning all RSS items (may be noisy!)');
    }
    
    // Sort by date (newest first)
    filteredItems.sort((a, b) => {
      const dateA = new Date(a.pubDate).getTime();
      const dateB = new Date(b.pubDate).getTime();
      return dateB - dateA;
    });
    
    // Limit to most recent 100
    filteredItems = filteredItems.slice(0, 100);
    
    console.log(`[LIVE] Fetched ${filteredItems.length} RSS feed items`);
    
    return filteredItems;
  } catch (error: any) {
    console.error('[LIVE] Error fetching RSS Feeds:', error);
    throw error;
  }
}

/**
 * Parse RSS XML feed
 */
function parseRSSFeed(xmlText: string, source: string): RSSFeedItem[] {
  const items: RSSFeedItem[] = [];
  
  try {
    // Simple XML parsing (for production, use a proper XML parser)
    const itemMatches = xmlText.matchAll(/<item[^>]*>([\s\S]*?)<\/item>/gi);
    
    for (const match of itemMatches) {
      const itemXml = match[1];
      
      const titleMatch = itemXml.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
      const descriptionMatch = itemXml.match(/<description[^>]*>([\s\S]*?)<\/description>/i);
      const linkMatch = itemXml.match(/<link[^>]*>([\s\S]*?)<\/link>/i);
      const pubDateMatch = itemXml.match(/<pubDate[^>]*>([\s\S]*?)<\/pubDate>/i);
      const guidMatch = itemXml.match(/<guid[^>]*>([\s\S]*?)<\/guid>/i);
      
      const title = titleMatch ? decodeHTMLEntities(titleMatch[1].trim()) : 'Untitled';
      const description = descriptionMatch ? decodeHTMLEntities(descriptionMatch[1].trim()) : '';
      const link = linkMatch ? linkMatch[1].trim() : '';
      const pubDate = pubDateMatch ? pubDateMatch[1].trim() : new Date().toISOString();
      const guid = guidMatch ? guidMatch[1].trim() : link || `${source}-${Date.now()}`;
      
      items.push({
        id: `rss-${guid}`,
        title,
        description,
        link,
        pubDate,
        source,
        guid,
      });
    }
  } catch (error) {
    console.error('Error parsing RSS feed:', error);
  }
  
  return items;
}

/**
 * Decode HTML entities (works in browser and Node.js)
 */
function decodeHTMLEntities(text: string): string {
  // Simple HTML entity decoding
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/<[^>]*>/g, ''); // Remove HTML tags
}

// ============================================================================
// Helper Functions
// ============================================================================

function extractKeywords(text: string): string[] {
  const commonWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by']);
  const words = text.toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 3 && !commonWords.has(word));
  
  return [...new Set(words)].slice(0, 10);
}

function matchServiceLines(text: string): string[] {
  const serviceLines: string[] = [];
  const lowerText = text.toLowerCase();
  
  const serviceLineKeywords: Record<string, string[]> = {
    'Environmental Assessment': [
      'environmental assessment', 'esa', 'phase i', 'phase ii', 'phase iii',
      'site assessment', 'contamination', 'remediation', 'environmental'
    ],
    'Engineering Services': [
      'engineering', 'design', 'permit', 'compliance', 'monitoring',
      'air quality', 'water quality', 'waste management'
    ],
    'Consulting Services': [
      'consulting', 'advisory', 'planning', 'feasibility', 'study'
    ],
    'Research & Development': [
      'research', 'development', 'rd&d', 'innovation', 'technology'
    ],
  };
  
  for (const [serviceLine, keywords] of Object.entries(serviceLineKeywords)) {
    if (keywords.some(keyword => lowerText.includes(keyword))) {
      serviceLines.push(serviceLine);
    }
  }
  
  return serviceLines.length > 0 ? serviceLines : ['General'];
}

